import {
  CBadge,
  CButton,
  CCol,
  CDataTable,
  CFormGroup,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CRow,
  CSelect,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { getWebhookEventList } from "../../api/webhook-events";
import {
  WebhookEvent,
  WEBHOOK_EVENT_TYPE_LIST,
  WEBHOOK_ENTITY_TYPE_LIST,
} from "../../models/webhook-endpoint";
import { SUCCESS } from "../../utils/constants/tags";
import { warningAlert } from "../utils/messages";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

interface WebhookEndpointEventLogProps {
  show: boolean;
  endpointId?: number;
  endpointName?: string;
  onClose: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "secondary",
  retrying: "warning",
  delivered: "success",
  failed: "danger",
  abandoned: "dark",
};

const STATUSES = ["pending", "retrying", "delivered", "failed", "abandoned"];

const WebhookEndpointEventLog: React.FC<WebhookEndpointEventLogProps> = ({
  show,
  endpointId,
  endpointName,
  onClose,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterEventType, setFilterEventType] = useState("");
  const [filterEntityType, setFilterEntityType] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const fields = [
    { key: "eventType", _classes: ["text-center"], label: "Tipo de evento" },
    { key: "entityType", _classes: ["text-center"], label: "Entidad" },
    { key: "entityId", _classes: ["text-center"], label: "ID" },
    { key: "status", _classes: ["text-center"], label: "Estado" },
    { key: "attemptCount", _classes: ["text-center"], label: "Intentos" },
    { key: "lastResponseStatus", _classes: ["text-center"], label: "HTTP" },
    { key: "createdAt", _classes: ["text-center"], label: "Creado" },
    { key: "deliveredAt", _classes: ["text-center"], label: "Entregado" },
  ];

  const fetchEvents = async (
    currentPage: number,
    status: string,
    eventType: string,
    entityType: string,
    dateFrom: string,
    dateTo: string,
  ) => {
    if (!taxPayer.id || !endpointId) return;
    setLoading(true);
    const result = await getWebhookEventList(
      taxPayer.id,
      endpointId,
      currentPage,
      status || undefined,
      eventType || undefined,
      entityType || undefined,
      dateFrom || undefined,
      dateTo || undefined,
    );
    if (result.status === SUCCESS) {
      if (result.data !== undefined) {
        setEvents(result.data.items);
        setCount(result.data.count);
      }
    } else {
      warningAlert(result.detail ?? "Error desconocido");
    }
    setLoading(false);
  };

  const onApplyFilters = () => {
    setPage(1);
    fetchEvents(
      1,
      filterStatus,
      filterEventType,
      filterEntityType,
      filterDateFrom,
      filterDateTo,
    );
  };

  const onClearFilters = () => {
    setFilterStatus("");
    setFilterEventType("");
    setFilterEntityType("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setPage(1);
    fetchEvents(1, "", "", "", "", "");
  };

  useEffect(() => {
    if (show && endpointId) {
      setPage(1);
      setFilterStatus("");
      setFilterEventType("");
      setFilterEntityType("");
      setFilterDateFrom("");
      setFilterDateTo("");
      fetchEvents(1, "", "", "", "", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, endpointId]);

  const totalPages = Math.ceil(count / 20);

  const onPrevPage = () => {
    const newPage = page - 1;
    setPage(newPage);
    fetchEvents(
      newPage,
      filterStatus,
      filterEventType,
      filterEntityType,
      filterDateFrom,
      filterDateTo,
    );
  };

  const onNextPage = () => {
    const newPage = page + 1;
    setPage(newPage);
    fetchEvents(
      newPage,
      filterStatus,
      filterEventType,
      filterEntityType,
      filterDateFrom,
      filterDateTo,
    );
  };

  return (
    <CModal show={show} onClose={onClose} size="xl">
      <CModalHeader closeButton>
        <CModalTitle>Historial de eventos: {endpointName ?? ""}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CRow className="mb-2 align-items-end">
          <CCol sm="12" md="3">
            <CFormGroup className="mb-0">
              <CLabel>Entidad</CLabel>
              <CSelect
                value={filterEntityType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setFilterEntityType(e.target.value)
                }
              >
                <option value="">Todas</option>
                {WEBHOOK_ENTITY_TYPE_LIST.map((et) => (
                  <option key={et.value} value={et.value}>
                    {et.label}
                  </option>
                ))}
              </CSelect>
            </CFormGroup>
          </CCol>
          <CCol sm="12" md="3">
            <CFormGroup className="mb-0">
              <CLabel>Tipo de evento</CLabel>
              <CSelect
                value={filterEventType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setFilterEventType(e.target.value)
                }
              >
                <option value="">Todos</option>
                {WEBHOOK_EVENT_TYPE_LIST.map((et) => (
                  <option key={et.value} value={et.value}>
                    {et.group} — {et.label}
                  </option>
                ))}
              </CSelect>
            </CFormGroup>
          </CCol>
          <CCol sm="12" md="2">
            <CFormGroup className="mb-0">
              <CLabel>Estado</CLabel>
              <CSelect
                value={filterStatus}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setFilterStatus(e.target.value)
                }
              >
                <option value="">Todos</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </CSelect>
            </CFormGroup>
          </CCol>
          <CCol sm="12" md="2">
            <CFormGroup className="mb-0">
              <CLabel>Desde</CLabel>
              <CInput
                type="date"
                value={filterDateFrom}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilterDateFrom(e.target.value)
                }
              />
            </CFormGroup>
          </CCol>
          <CCol sm="12" md="2">
            <CFormGroup className="mb-0">
              <CLabel>Hasta</CLabel>
              <CInput
                type="date"
                value={filterDateTo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilterDateTo(e.target.value)
                }
              />
            </CFormGroup>
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol sm="12" className="d-flex">
            <CButton color="primary" className="mr-2" onClick={onApplyFilters}>
              Filtrar
            </CButton>
            <CButton color="secondary" onClick={onClearFilters}>
              Limpiar
            </CButton>
          </CCol>
        </CRow>

        <CDataTable
          noItemsView={{
            noItems: "No se encontraron eventos",
            noResults: "No se encontraron eventos",
          }}
          addTableClasses={"table-fixed"}
          fields={fields}
          items={events}
          striped
          border
          loading={loading}
          responsive
          scopedSlots={{
            eventType: (item: WebhookEvent) => (
              <td className="text-center">
                <code>{item.eventType ?? "-"}</code>
              </td>
            ),
            entityType: (item: WebhookEvent) => (
              <td className="text-center">{item.entityType ?? "-"}</td>
            ),
            entityId: (item: WebhookEvent) => (
              <td className="text-center">{item.entityId ?? "-"}</td>
            ),
            status: (item: WebhookEvent) => (
              <td className="text-center">
                <CBadge color={STATUS_COLORS[item.status ?? ""] ?? "secondary"}>
                  {item.status ?? "-"}
                </CBadge>
              </td>
            ),
            attemptCount: (item: WebhookEvent) => (
              <td className="text-center">{item.attemptCount ?? 0}</td>
            ),
            lastResponseStatus: (item: WebhookEvent) => (
              <td className="text-center">{item.lastResponseStatus ?? "-"}</td>
            ),
            createdAt: (item: WebhookEvent) => (
              <td className="text-center">
                {item.createdAt
                  ? new Date(item.createdAt).toLocaleString()
                  : "-"}
              </td>
            ),
            deliveredAt: (item: WebhookEvent) => (
              <td className="text-center">
                {item.deliveredAt
                  ? new Date(item.deliveredAt).toLocaleString()
                  : "-"}
              </td>
            ),
          }}
        />

        {totalPages > 1 && (
          <CRow className="mt-2">
            <CCol sm="12" className="d-flex justify-content-center">
              <CButton
                color="secondary"
                disabled={page <= 1}
                onClick={onPrevPage}
                className="mr-2"
              >
                Anterior
              </CButton>
              <span className="align-self-center">
                {page} / {totalPages}
              </span>
              <CButton
                color="secondary"
                disabled={page >= totalPages}
                onClick={onNextPage}
                className="ml-2"
              >
                Siguiente
              </CButton>
            </CCol>
          </CRow>
        )}
      </CModalBody>
    </CModal>
  );
};

export default WebhookEndpointEventLog;
